import React, {useEffect, useState} from 'react';
import {app, dialog, copydir, fs, fsPromises, ipcRenderer, isDevMode} from './electron-modules';
import {store} from './redux/store';
import slash from 'slash';
import path from 'path';
import { AssetType, selectAllAssets, getCameraFileAssets, getOrthoAssets, getPointCloudAssets, getModelAssets } from './redux/assets-slice';
import LocalScene from './viewer/js/projections';
import {PythonExecutable, trackToolUsage} from './executable';
import {useSelector} from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  LinearProgressProps,
  makeStyles,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import { AlertDialog } from './dialogs';
import { openDirectory } from './asset-utils';
import {Asset} from './redux/assets-slice';
import sanitize from "sanitize-filename";
import { selectName } from './redux/project-slice';
import { toast } from './app';
import { updateFromJSON } from './project-utils';

const processTimeout = 1000;
const dataPathName = "asset-data";
const pointsFolder = "points";
const modelsFolder = "models";
const imagesFolder = "images";
const dialogTitle = "Data Export";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    warning: {
      color: theme.palette.warning.main
    },
    exportText: {
      padding: theme.spacing(1)
    },
    contentCenter: {
      textAlign: "center"
    },
    cancelProgress: {
      marginRight: theme.spacing(1)
    },
    cancelContent: {
      paddingBottom: theme.spacing(3)
    }
  }),
);

const LinearProgressWithLabel = (props: LinearProgressProps & { value: number }) => {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" color="secondary" {...props} />
      </Box>
      <Box minWidth={35}>
        <Typography variant="body2" color="textSecondary">{`${Math.round(
          props.value,
        )}%`}</Typography>
      </Box>
    </Box>
  );
};

const initialState = {
  rootOutputPath: null,
  webOutputPath: null,
  assets: [] as Asset[],
  numAssets: 0,
  currentIndex: 0
};

export const StaticExporter = (props) => {
  const {exportModalOpen, setExportModalOpen} = props;

  const theme = useTheme();
  const classes = useStyles(theme);

  const assets = useSelector(selectAllAssets);
  const orthoFiles = getOrthoAssets(assets);
  const pointClouds = getPointCloudAssets(assets);
  const camerasFiles = getCameraFileAssets(assets);
  const modelFiles = getModelAssets(assets);

  const projectName = useSelector(selectName);

  const [exportName, setExportName] = useState("");
  const [dataExportOpen, setDataExportOpen] = useState(false);
  const [runnable, setRunnable] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [runningExe, setRunningExe] = useState(null);
  const [exportTopMessage, setExportTopMessage] = useState("");
  const [exportBottomMessage, setExportBottomMessage] = useState("");
  const [exportPercent, setExportPercent] = useState(0);
  const [exportAssets, setExportAssets] = useState<{
    rootOutputPath: string | null,
    webOutputPath: string | null
    assets: Asset[]
    numAssets: number
    currentIndex: number
  }>(initialState);

  const numClouds = pointClouds.length;
  const numCameras = camerasFiles.length;
  const numOrthos = orthoFiles.length;
  const numModels = modelFiles.length;
  const canExport = (numClouds > 0) || (numCameras > 0) || (numOrthos > 0) || (numModels > 0);
  const numRawPointcloud = pointClouds.filter(x => x.type === AssetType.LAS).length;

  const handleOpen = () => {
    setExportModalOpen(true);
  };

  const handleClose = () => {
    setExportModalOpen(false);
  };

  const onExportFinished = () => {
    setRunnable(false);
    setDataExportOpen(false);
  };

  const handleExportCancel = () => {
    onExportFinished();
    setCancelling(true);
    setNewRunningExe(null);
  };

  const setNewRunningExe = (exe) => {
    runningExe?.destroy();
    setRunningExe(exe);
  };

  const handleExport = () => {
    handleClose();
    exportToHtml();
  };

  const setExportMessage = (top = "", bottom = "") => {
    setExportTopMessage(top);
    setExportBottomMessage(bottom);
  };

  const getAssetIndex = (state, asset) => {
    const assetIds = state.assets.map(x => x.id);
    const index = assetIds.indexOf(asset.id);
    return index;
  };

  const createNewFolder = async (folderPath) => {
    await deleteFolder(folderPath);
    await fsPromises.mkdir(folderPath);
  };

  const deleteFolder = async (folderPath) => {
    const folderExists = fs.existsSync(folderPath);
    if (folderExists) {
      await fsPromises.rmdir(folderPath, {recursive: true});
    }
  };

  const getModelFilename = (asset) => {
    const extension = path.extname(asset.name).slice(1);
    return `${asset.id}.${extension}`;
  };

  const moveCameraData = async (asset, dataPath) => {
    const output = `${imagesFolder}-${asset.id}`;
    const imagesPath = path.join(dataPath, output);

    await createNewFolder(imagesPath);

    return new Promise(async (resolve) => {
      const imagesData = asset.data;
      for (let i=0;i<imagesData.length; i++) {
        const imageData = imagesData[i];
        const imageName = path.basename(imageData.path);
        const newImagePath = path.join(imagesPath, imageName);

        if (fs.existsSync(imageData.path)) {
          await fsPromises.copyFile(imageData.path, newImagePath);
        }
      };

      setTimeout(() => {
        resolve(true);
      }, processTimeout);
    });
  };

  const moveEncompassData = async (asset, dataPath) => {
    const inputPath = asset.data.path;
    const outputPath = path.join(
      dataPath, `${pointsFolder}-${asset.id}`
    );
    return copyToFolder(asset, inputPath, outputPath);
  };

  const moveRawPointcloud = async (asset, dataPath) => {
    const units = LocalScene.dataProjectionUnits;
    const inputPath = asset.data.path;
    const output = `${pointsFolder}-${asset.id}`;
    const pointPath = path.join(dataPath, output);

    return new Promise((resolve) => {
      let commands = [
        "-p", "process_las",
        "--input_path", inputPath,
        "--output_folder", pointPath,
        "--units", units
      ];

      const exe = new PythonExecutable();
      setNewRunningExe(exe);

      exe.run({
        command: commands,
        parseLineData: false,
        onLine: text => {
          setExportBottomMessage(text);
        },
        onClose: () => {
          setTimeout(() => {
            resolve(true);
          }, processTimeout);
        }
      });
    });
  };

  const moveModelData = async (asset, dataPath) => {
    const inputPath = asset.data.path;
    const outputFolder = path.join(dataPath, modelsFolder);
    const outputPath = path.join(
      outputFolder, getModelFilename(asset)
    );

    await createNewFolder(outputFolder);
    return copyToFile(asset, inputPath, outputPath);
  };

  const copyToFile = (asset, inputPath, outputPath) => {
    return new Promise(async (resolve) => {
      if (fs.existsSync(inputPath)) {
        await fsPromises.copyFile(inputPath, outputPath);
      } else {
        dialog.showErrorBox('Error', `An error occurred while exporting ${asset.name}`);
        return;
      }

      setTimeout(() => {
        resolve(true);
      }, processTimeout);
    });
  };

  const copyToFolder = (asset, inputPath, outputPath) => {
    return new Promise((resolve) => {
      copydir(inputPath, outputPath, {}, err => {
        if (err) {
          dialog.showErrorBox('Error', `An error occurred while exporting ${asset.name}`);
          return;
        }

        setTimeout(() => {
          resolve(true);
        }, processTimeout);
      });
    });
  };

  const updateCameraDataState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);
    const output = `${imagesFolder}-${asset.id}`;

    const imagesData = asset.data;
    imagesData.forEach((imageData, imageIndex) => {
      const imageName = path.basename(imageData.path);
      const finalPath = path.join(dataPathName, output, imageName);
      state.assets[assetIndex].data[imageIndex].path = `./${finalPath}`;
    });
  };

  const updateModelDataState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);
    const finalPath = path.join(
      dataPathName,
      modelsFolder,
      getModelFilename(asset)
    );

    state.assets[assetIndex].data.path = `./${finalPath}`;
  };

  const updateEncompassDataState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);
    const output = `${pointsFolder}-${asset.id}`;
    const finalPath = path.join(dataPathName, output);
    state.assets[assetIndex].data.path = `./${finalPath}`;
  };

  const updateRawPointcloudDataState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);
    const output = `${pointsFolder}-${asset.id}`;
    const finalPath = path.join(dataPathName, output, "points");
    state.assets[assetIndex].data.path = `./${finalPath}`;
    state.assets[assetIndex].type = AssetType.Encompass;
  };

  const updateCameraTransformPath = (state, outputPath) => {
    const dataPath = path.join(outputPath, dataPathName);

    const outputName = "camera.4x4";
    const matrixPath = state.camera.transform;
    const newImagePath = path.join(dataPath, outputName);

    if (matrixPath) {
      fsPromises.copyFile(matrixPath, newImagePath);
      const finalPath = path.join(dataPathName, outputName);
      state.camera.transform = `./${finalPath}`;
    }
  };

  const moveAssetData = async (asset, outputPath) => {
    const dataPath = path.join(outputPath, dataPathName);

    if (asset.type === AssetType.Encompass) {
      await moveEncompassData(asset, dataPath);
    } else if (asset.type === AssetType.LAS || asset.type === AssetType.E57Points) {
      await moveRawPointcloud(asset, dataPath);
    } else if (asset.type === AssetType.Panoramic) {
      await moveCameraData(asset, dataPath);
    } else if (asset.type === AssetType.Planar) {
      await moveCameraData(asset, dataPath);
    } else if (asset.type === AssetType.IFC) {
      await moveModelData(asset, dataPath);
    }
  };

  const finishExport = (webOutputPath, rootOutputPath) => {
    setExportMessage("Export project state");

    // Get a deep copy of the project state
    let state = JSON.parse(JSON.stringify(store.getState()));

    // Update the fromJSON state
    state = updateFromJSON(state);

    const assets = exportAssets.assets;

    assets.forEach(asset => {
      if (asset.type === AssetType.Encompass) {
        updateEncompassDataState(asset, state);
      } else if (asset.type === AssetType.LAS || asset.type === AssetType.E57Points) {
        updateRawPointcloudDataState(asset, state);
      } else if (asset.type === AssetType.Panoramic) {
        updateCameraDataState(asset, state);
      } else if (asset.type === AssetType.Planar) {
        updateCameraDataState(asset, state);
      } else if (asset.type === AssetType.IFC) {
        updateModelDataState(asset, state);
      }
    });

    // Update camera transform path
    updateCameraTransformPath(state, webOutputPath);

    setExportPercent(100);

    const projectJson = JSON.stringify(state, null, 2);
    const projectPath = path.join(webOutputPath, "static.json");

    const miniViewerPath = getMiniViewerPath();
    const exeOutputPath = path.join(rootOutputPath, path.basename(miniViewerPath));
    fs.copyFileSync(miniViewerPath, exeOutputPath);

    fs.writeFile(projectPath, projectJson, (err) => {
      if (err) {
        dialog.showErrorBox('Error', 'An error occurred while exporting static html');
      }

      setTimeout(() => {
        setDataExportOpen(false);
        setNewRunningExe(null);
        setSuccessOpen(true);
      }, processTimeout);
    });
  };

  const exportToHtml = () => {
    const buildPath = getBuildPath();

    trackToolUsage("exporter");

    openDirectory().then(async outputFolder => {
      if (!outputFolder) {
        return;
      }

      setExportPercent(0);
      setDataExportOpen(true);
      setExportMessage("Creating output folder");

      // Create final output folder
      const rootOutputPath = path.join(outputFolder, exportName);
      await createNewFolder(rootOutputPath);

      // Create final output folder
      const webOutputPath = path.join(rootOutputPath, "web");
      await createNewFolder(webOutputPath);

      setExportMessage("Copy html files output folder");

      copydir(buildPath, webOutputPath, {}, err => {
        if (err) {
          dialog.showErrorBox('Error', 'An error occurred while exporting static html');
          return;
        }

        setExportMessage("Preparing data to export");

        setTimeout(async () => {
          const dataPath = path.join(webOutputPath, dataPathName);
          await createNewFolder(dataPath);

          const assets = [
            ...pointClouds,
            ...camerasFiles,
            ...modelFiles
          ];

          console.log("Assets to export:", assets);

          if (assets.length === 0) {
            // Skip directly to final step
            finishExport(webOutputPath, rootOutputPath);
            return;
          }

          setExportAssets({
            webOutputPath: webOutputPath,
            rootOutputPath: rootOutputPath,
            assets: assets,
            numAssets: assets.length,
            currentIndex: 0
          });
          setRunnable(true);
        }, processTimeout);
      });
    });
  };

  const getBuildPath= () => {
    const appPath = slash(app.getAppPath());
    const buildPath = isDevMode
      ? path.join(appPath, "build")
      : path.join(path.dirname(appPath), "app.asar.unpacked", "build");

    return buildPath;
  };

  const getMiniViewerPath= () => {
    const appPath = slash(app.getAppPath());
    const buildPath = isDevMode
      ? path.join(appPath, "miniviewer.exe")
      : path.join(path.dirname(appPath), "app.asar.unpacked", "miniviewer.exe");

    return buildPath;
  };

  const lineItemText = (text, numItems) => {
    return numItems > 0 ?
      `<li>${text}${numItems !== 1 ? "s" : ""} <b>(${numItems})</b> </li>`
      : "";
  };

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-static-export");
    ipcRenderer?.on("open-static-export", () => {
      if (canExport) {
        handleOpen();
        return;
      }

      toast.error("No data loaded");
    });
  }, [ipcRenderer, canExport]);

  useEffect(() => {
    setExportName(`export [${sanitize(projectName)}]`);
  }, [projectName]);

  useEffect(() => {
    const currentIndex = exportAssets.currentIndex;
    const numAssets = exportAssets.numAssets;
    const webOutputPath = exportAssets.webOutputPath;
    const assets = exportAssets.assets;
    const toProcess = assets[currentIndex];

    if (!toProcess) {
      return;
    }

    if (!runnable) {
      return;
    }

    // Process one asset from the list
    const processNextAsset = async () => {
      const message = `Processing data: ${toProcess.name}`;

      setExportTopMessage(message);
      console.log(message);

      await moveAssetData(toProcess, webOutputPath);

      const nextIndex = currentIndex + 1;
      const percent = 100.0 * nextIndex / numAssets;
      setExportPercent(percent);
      setExportAssets({
        ...exportAssets,
        currentIndex: nextIndex
      });

      if (nextIndex === numAssets) {
        setFinishing(true);
      } else {
        setCancelling(false);
      }
    };
    processNextAsset();
  }, [exportAssets, runnable]);

  useEffect(() => {
    if (cancelling) {
      setCancelOpen(true);
    } else {
      setTimeout(async () => {
        // Delete output folder and close modal
        const webOutputPath = exportAssets.webOutputPath;
        await deleteFolder(webOutputPath);
        setCancelOpen(false);
      }, processTimeout);
    }
  }, [cancelling]);

  useEffect(() => {
    if (!finishing) {
      return;
    }

    if (!cancelling) {
      // Reached the end and not cancelling export
      const webOutputPath = exportAssets.webOutputPath;
      const rootOutputPath = exportAssets.rootOutputPath;
      finishExport(webOutputPath, rootOutputPath);
    }

    setCancelling(false);
    setFinishing(false);
  }, [finishing]);

  const pointListItem = lineItemText("Point Cloud", numClouds);
  const cameraListItem = lineItemText("Camera File", numCameras);
  const orthoListItem = lineItemText("Orthomosaic", numOrthos);
  const modelListItem = lineItemText("Model", numModels);

  return (
    <React.Fragment>
      {/* Exporting dialog */}
      <Dialog
        open={dataExportOpen}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{"Exporting HTML"}</DialogTitle>

        <DialogContent className={classes.contentCenter}>
          <Typography variant="body1" className={classes.exportText}>
            {exportTopMessage}
          </Typography>

          <Typography variant="body2" className={classes.exportText}>
            {exportBottomMessage}
          </Typography>

          <LinearProgressWithLabel value={exportPercent} />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleExportCancel}
            color="primary"
            disabled={(runnable === false) || (exportPercent === 100)}
          >
            Cancel Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main dialog */}
      <Dialog
        open={exportModalOpen}
        onClose={handleClose}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>

        <DialogContent>
          <DialogContentText dangerouslySetInnerHTML={{
            __html: `
              A folder named <b>${exportName}</b> will be created.
              <br><br>
              Export data includes:
              <ul>
                ${pointListItem}
                ${cameraListItem}
                ${orthoListItem}
                ${modelListItem}
              </ul>
            `
          }}/>

          {(numRawPointcloud > 0) && (<DialogContentText className={classes.warning}>
            This process may take some time due to the conversion process.
          </DialogContentText>)}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>

          <Button
            onClick={handleExport}
            color="primary"
            disabled={!canExport}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancelling dialog */}
      <Dialog
        open={cancelOpen}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent className={classes.cancelContent}>
          <CircularProgress size="1em" className={classes.cancelProgress}/>
          <DialogContentText display="inline">
            Cancelling export
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* Success dialog */}
      <AlertDialog
        open={successOpen}
        title={dialogTitle}
        handleClose={() => {
          setSuccessOpen(false);
        }}
      >
        <DialogContentText>
          Successfully exported data to static HTML
        </DialogContentText>
      </AlertDialog>
    </React.Fragment>
  );
};
